/** Every amount the API sends is in micro-USD: 1 000 000 is one dollar. */
export const MICROS_PER_DOLLAR = 1_000_000;

/** e.g. "$1.25". */
export function dollars(micros: number): string {
  return usd(micros / MICROS_PER_DOLLAR);
}

/** Like {@link dollars}, but tiny amounts (a single AI call costs fractions of a cent)
 * get four decimals instead of all reading "$0.00". */
export function preciseDollars(micros: number): string {
  return micros > 0 && micros < 10_000
    ? `$${(micros / MICROS_PER_DOLLAR).toFixed(4)}`
    : dollars(micros);
}

/** {@link preciseDollars}, or "—" when the amount is unknown. */
export function maybeDollars(micros: number | null | undefined): string {
  return micros == null ? "—" : preciseDollars(micros);
}

/** An amount already in dollars (OpenRouter's quota), or "—" when unknown. */
export function usd(value: number | null | undefined): string {
  return value == null ? "—" : `$${value.toFixed(2)}`;
}

/** An estimate, e.g. "~$0.040", or "—" when unknown. */
export function estimatedDollars(micros: number | null | undefined): string {
  return micros == null ? "—" : `~$${(micros / MICROS_PER_DOLLAR).toFixed(3)}`;
}

/** A budget field's value: micro-USD to dollars, rounded to the cent. */
export function toDollars(micros: number): number {
  return Math.round((micros / MICROS_PER_DOLLAR) * 100) / 100;
}

/** The inverse of {@link toDollars}. */
export function toMicros(value: number): number {
  return Math.round(value * MICROS_PER_DOLLAR);
}

/** e.g. "$0.30" for a price in micro-USD per million tokens, "free", or "—" when
 * unknown. Below $10 two decimals keep real differences between models visible. */
export function perMillionTokens(microsPerMtok: number | null | undefined): string {
  if (microsPerMtok == null) {
    return "—";
  }
  const value = microsPerMtok / MICROS_PER_DOLLAR;
  if (value === 0) {
    return "free";
  }
  return value < 0.01
    ? `$${value.toFixed(4)}`
    : value < 10
      ? `$${value.toFixed(2)}`
      : `$${value.toFixed(0)}`;
}
